import { useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import SiteEditor from "@/components/admin/SiteEditor";
import ServicesEditor from "@/components/admin/ServicesEditor";
import GalleryEditor from "@/components/admin/GalleryEditor";
import MessagesViewer from "@/components/admin/MessagesViewer";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { reload } = useSite();
  const [tab, setTab] = useState("site");

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 font-sans">
      <header className="bg-white border-b border-zinc-300 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-zinc-900 border-2 border-brand-red flex items-center justify-center text-white font-display font-black text-sm">
              GBS
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-base tracking-tight">{t("admin.dashboard")}</div>
              <div className="tech-label !text-[10px]">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              target="_blank"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-brand-red px-3 py-2"
              data-testid="admin-view-site"
            >
              View site <ExternalLink size={14} />
            </Link>
            <Button
              data-testid="admin-logout"
              variant="outline"
              onClick={() => { logout(); window.location.href = "/admin/login"; }}
              className="rounded-none border-zinc-300"
            >
              <LogOut size={14} className="mr-2" />
              {t("admin.signOut")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-zinc-300 rounded-none h-auto p-0" data-testid="admin-tabs">
            <TabsTrigger value="site" data-testid="tab-site" className="rounded-none px-5 py-3 data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
              {t("admin.tabs.site")}
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className="rounded-none px-5 py-3 data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
              {t("admin.tabs.services")}
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-none px-5 py-3 data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
              {t("admin.tabs.gallery")}
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-none px-5 py-3 data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
              {t("admin.tabs.messages")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="site" className="mt-6">
            <SiteEditor onSaved={reload} />
          </TabsContent>
          <TabsContent value="services" className="mt-6">
            <ServicesEditor onChanged={reload} />
          </TabsContent>
          <TabsContent value="gallery" className="mt-6">
            <GalleryEditor onChanged={reload} />
          </TabsContent>
          <TabsContent value="messages" className="mt-6">
            <MessagesViewer />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
